import type { ApolloError } from '@apollo/client';
import whiteHoverLogo from '@hover/tokens-brand/assets/logo-full--white.svg';
import type { PropsWithChildren } from 'react';
import React, { createContext, useContext, useState } from 'react';

import type { ProfileQuery } from 'src/shared/graphql/generated';
import { useProfileQuery } from 'src/shared/graphql/generated';
import { logError } from 'src/shared/telemetry/logger';

interface BrandContext {
  primaryColor: string;
  secondaryColor: string;
  logo: string;
  loadingBrand: boolean;
}

const initialBrandContext: BrandContext = {
  logo: whiteHoverLogo,
  primaryColor: 'primary.600',
  secondaryColor: 'initial',
  loadingBrand: true,
};

const context = createContext<BrandContext>(initialBrandContext);

const useBrand = () => useContext(context);

interface BrandProviderProps extends PropsWithChildren<{}> {}

const BrandProvider = ({ children }: BrandProviderProps) => {
  const [brand, setBrand] = useState<BrandContext>(initialBrandContext);
  const setBrandOnSuccess = (data: ProfileQuery) => {
    const { placesLogoUrl, primaryColor, secondaryColor } =
      data?.profile?.user?.orgs[0]?.brand || {};

    const customerBrand: BrandContext = {
      logo: placesLogoUrl || brand.logo,
      primaryColor: primaryColor || brand.primaryColor,
      secondaryColor: secondaryColor || brand.secondaryColor,
      loadingBrand: false,
    };

    setBrand(customerBrand);
  };
  const setBrandOnError = (error: ApolloError) => {
    setBrand({ ...brand, loadingBrand: false });
    logError(error);
  };

  useProfileQuery({
    onCompleted: setBrandOnSuccess,
    onError: setBrandOnError,
  });

  return <context.Provider value={brand}>{children}</context.Provider>;
};

export { BrandProvider, useBrand };
